import { Card, Flex, Progress, Skeleton, Spin, Tooltip, Typography } from "antd"
import { useCallback, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router"

import { useAppDispatch } from "../../../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../../../hooks/use-app-selector.ts"
import { getProgressColor } from "../../../../../Projects/constants/project-progress.ts"
import { loadMilestoneSnapshot } from "../../../../redux/project-page-reads.ts"
import {
  EMPTY_CHECKLIST,
  EMPTY_MILESTONES
} from "../../../../redux/project.slice.ts"
import { fetchProjectMilestoneSnapshot } from "../../../../redux/project-milestones/project-milestones.thunks.ts"
import { FETCHING_SNAPSHOT_ENUM } from "../../../../redux/project.types.ts"
import {
  type MarginHalf,
  type MarginLevel,
  type ProjectRiskLevel
} from "../../../../redux/project-milestones/project-milestones.types.ts"

const { Title, Text } = Typography

const ABSENT = "—"

type Tone = "positive" | "warning" | "negative" | "neutral"

const MARGIN_LABEL: Record<MarginLevel, string> = {
  healthy: "Healthy",
  tight: "Tight",
  short: "Short",
  no_data: "No data"
}

const MARGIN_TONE: Record<MarginLevel, Tone> = {
  healthy: "positive",
  tight: "warning",
  short: "negative",
  no_data: "neutral"
}

const RISK_LABEL: Record<ProjectRiskLevel, string> = {
  on_track: "On Track",
  at_risk: "At Risk",
  high_risk: "High Risk",
  unknown: "No due dates"
}

const RISK_TONE: Record<ProjectRiskLevel, Tone> = {
  on_track: "positive",
  at_risk: "warning",
  high_risk: "negative",
  unknown: "neutral"
}

function delayTone(days: number | null): Tone {
  if (days === null) return "neutral"
  if (days > 0) return "negative"
  return "positive"
}

const TONE_CLASS: Record<Tone, string> = {
  positive: "text-green-500",
  warning: "text-orange-500",
  negative: "text-red-500",
  neutral: ""
}

interface TileProps {
  testId: string
  label: string
  value: string
  hint?: string
  tone?: Tone
}

function Tile({ testId, label, value, hint, tone = "neutral" }: TileProps) {
  return (
    <div className="flex-1 min-w-0" data-testid={testId} data-tone={tone}>
      <Text type="secondary" className="text-xs block truncate">
        {label}
      </Text>
      <Text className={`text-base font-semibold block ${TONE_CLASS[tone]}`}>
        {value}
      </Text>
      {hint ? (
        <Text type="secondary" className="text-xs block truncate">
          {hint}
        </Text>
      ) : null}
    </div>
  )
}

interface MarginRowProps {
  testId: string
  label: string
  half: MarginHalf
}

function marginRatio(half: MarginHalf) {
  const payables = Number(half.payables)
  const receivables = Number(half.receivables)
  if (!Number.isFinite(payables) || payables === 0) return null
  if (!Number.isFinite(receivables)) return null
  return receivables / payables
}

function MarginRow({ testId, label, half }: MarginRowProps) {
  const { t } = useTranslation()
  const tone = MARGIN_TONE[half.level]
  const ratio = marginRatio(half)

  return (
    <Flex
      justify="space-between"
      align="baseline"
      gap={4}
      className="whitespace-nowrap"
      data-testid={testId}
      data-tone={tone}
    >
      <Text type="secondary" className="text-xs whitespace-nowrap">
        {label}
      </Text>
      <Flex align="baseline" gap={4} className="whitespace-nowrap">
        <Text
          className={`text-sm font-semibold whitespace-nowrap ${TONE_CLASS[tone]}`}
        >
          {ratio === null ? ABSENT : `${ratio.toFixed(2)}x`}
        </Text>
        <Text type="secondary" className="text-xs whitespace-nowrap">
          {t(
            `projectIndicators.cashflowLevel.${half.level}`,
            MARGIN_LABEL[half.level]
          )}
        </Text>
      </Flex>
    </Flex>
  )
}

function percentText(percent: number | null, suffix = "") {
  return percent === null ? ABSENT : `${percent}%${suffix}`
}

export function ProjectViewIndicators() {
  const projectId = useParams()?.id as string
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  // Depending on the paginated objects themselves refetches on every list
  // load; only a change in how much is done can move these figures. A list
  // not read yet for this project is no figure at all, so its first load is
  // not mistaken for a change.
  const milestoneCount = useAppSelector((state) =>
    state?.project?.milestones === EMPTY_MILESTONES
      ? undefined
      : state?.project?.milestones?.results?.length
  )
  const checklistCount = useAppSelector((state) =>
    state?.project?.checklistItems === EMPTY_CHECKLIST
      ? undefined
      : state?.project?.checklistItems?.results?.length
  )
  // Every Finance-tab write re-fetches this into Redux, so it doubles as the refetch signal.
  const financialSnapshot = useAppSelector(
    (state) => state?.project?.financialTransactionSnapshot
  )

  const pageDataLoading = useAppSelector(
    (state) =>
      state?.project?.milestonesRequestId != null ||
      !!state?.project?.fetchingSnapshotMap?.[FETCHING_SNAPSHOT_ENUM.FINANCIAL]
  )

  const data = useAppSelector((state) => state?.project?.milestoneSnapshot)
  const readStatus = useAppSelector((state) => {
    const read = state?.project?.pageReads?.milestoneSnapshot
    return read?.projectId === projectId ? read.status : undefined
  })
  const fetching = readStatus === "pending"
  const loadedOnce = readStatus === "done" || readStatus === "failed"
  const refetchSignals = useRef<unknown[] | null>(null)

  const handleFetch = useCallback(() => {
    void dispatch(fetchProjectMilestoneSnapshot(projectId))
  }, [dispatch, projectId])

  useEffect(() => {
    refetchSignals.current = null
    void dispatch(loadMilestoneSnapshot(projectId))
  }, [dispatch, projectId])

  // The first settled values are the page's initial load, which the mount fetch already reflects.
  useEffect(() => {
    if (!loadedOnce || pageDataLoading) return

    const signals = [milestoneCount, checklistCount, financialSnapshot]
    const previous = refetchSignals.current
    refetchSignals.current = signals

    if (
      previous &&
      signals.some(
        (signal, index) =>
          previous[index] !== undefined && signal !== previous[index]
      )
    ) {
      handleFetch()
    }
  }, [
    handleFetch,
    loadedOnce,
    pageDataLoading,
    milestoneCount,
    checklistCount,
    financialSnapshot
  ])

  if (!data && !loadedOnce) {
    return (
      <Card
        title={
          <Title level={5}>
            {t("projectIndicators.title", "Project Indicators")}
          </Title>
        }
        size="small"
      >
        <Skeleton.Node active className={"w-full h-36"} />
      </Card>
    )
  }

  const progress = data?.progress_percent ?? null
  const milestoneTile = data?.milestones
  const taskTile = data?.tasks
  const onTime = data?.on_time
  const risk = data?.risk
  const delayDays = data?.potential_delay?.days ?? null
  const riskPercent = risk?.percent ?? null
  const cashflow = data?.cashflow_health
  const onTimePercent = onTime?.percent ?? null

  return (
    <Spin spinning={fetching} size="large">
      <Card
        title={
          <Title level={5}>
            {t("projectIndicators.title", "Project Indicators")}
          </Title>
        }
        size="small"
        classNames={{ body: "p-2" }}
      >
        <div data-testid="indicators-progress">
          <Flex justify="space-between">
            <Text>{t("projectIndicators.progress", "Project Progress")}</Text>
            <Text className="shrink-0 font-semibold">
              {percentText(progress)}
            </Text>
          </Flex>
          <Progress
            percent={progress ?? 0}
            showInfo={false}
            strokeColor={getProgressColor(progress ?? 0)}
            size="small"
          />
        </div>

        <Flex gap={12} className="mt-2">
          <Tile
            testId="indicator-milestones"
            label={t("projectIndicators.milestones", "Milestones")}
            value={
              milestoneTile?.percent === null || !milestoneTile
                ? ABSENT
                : `${milestoneTile.completed} / ${milestoneTile.total}`
            }
            hint={
              milestoneTile?.percent === null || !milestoneTile
                ? undefined
                : t(
                    "projectIndicators.percentComplete",
                    "{{percent}}% complete",
                    {
                      percent: milestoneTile.percent
                    }
                  )
            }
          />
          <Tile
            testId="indicator-tasks"
            label={t("projectIndicators.tasks", "Tasks")}
            value={
              taskTile?.percent === null || !taskTile
                ? ABSENT
                : `${taskTile.completed} / ${taskTile.total}`
            }
            hint={
              taskTile?.percent === null || !taskTile
                ? undefined
                : t(
                    "projectIndicators.percentComplete",
                    "{{percent}}% complete",
                    {
                      percent: taskTile.percent
                    }
                  )
            }
          />
          <Tile
            testId="indicator-on_time"
            label={t("projectIndicators.onTime", "On Time")}
            value={percentText(onTimePercent)}
            hint={
              onTimePercent === null
                ? undefined
                : t("projectIndicators.onTimeHint", "on time")
            }
          />
        </Flex>

        <Flex gap={12} className="mt-2">
          <Tile
            testId="indicator-risk"
            label={t("projectIndicators.risk", "Project Risk")}
            value={percentText(
              riskPercent,
              ` ${t("projectIndicators.riskSuffix", "Risk")}`
            )}
            hint={
              !risk || (riskPercent === null && risk.level !== "unknown")
                ? undefined
                : t(
                    `projectIndicators.riskLevel.${risk.level}`,
                    RISK_LABEL[risk.level]
                  )
            }
            tone={
              riskPercent === null || !risk ? "neutral" : RISK_TONE[risk.level]
            }
          />
          <Tile
            testId="indicator-delay"
            label={t("projectIndicators.potentialDelay", "Potential Delay")}
            value={
              delayDays === null
                ? ABSENT
                : t("projectIndicators.days", "{{sign}}{{days}} days", {
                    sign: delayDays > 0 ? "+" : "",
                    days: delayDays
                  })
            }
            tone={delayTone(delayDays)}
          />
          {cashflow?.settled && cashflow?.outstanding ? (
            <div className="flex-1 min-w-0" data-testid="indicator-cashflow">
              <Flex align="center" gap={4} className="whitespace-nowrap">
                <Text type="secondary" className="text-xs whitespace-nowrap">
                  {t("projectIndicators.cashflow", "Cash Flow Health")}
                </Text>
                <Tooltip
                  title={t(
                    "projectIndicators.cashflowHint",
                    "Settled compares money received against money paid out; Outstanding compares what is still to bill against cost still to come. Green at or above cost x {{multiplier}}, orange within {{warning}}% below it, red further below.",
                    {
                      multiplier: Number(
                        cashflow.margin_multiplier ?? 3
                      ).toFixed(2),
                      warning: Number(
                        cashflow.margin_warning_percent ?? 20
                      ).toFixed(0)
                    }
                  )}
                >
                  <span className="text-xs text-gray-400 cursor-help">
                    &#9432;
                  </span>
                </Tooltip>
              </Flex>
              <MarginRow
                testId="indicator-cashflow-settled"
                label={t("milestoneMoney.settled", "Settled")}
                half={cashflow.settled}
              />
              <MarginRow
                testId="indicator-cashflow-outstanding"
                label={t(
                  "projectIndicators.cashflowOutstanding",
                  "Outstanding"
                )}
                half={cashflow.outstanding}
              />
            </div>
          ) : (
            <div className="flex-1 min-w-0" aria-hidden />
          )}
        </Flex>
      </Card>
    </Spin>
  )
}
